/**
 * Machine-readable error type returned by policy-frontend-api when an
 * external agent attempts to apply/save a backdated endorsement effective
 * date. Colocated with the predicate so the backend contract string lives
 * next to the logic that reacts to it rather than inside a component.
 */
export const EXTERNAL_AGENT_BACKDATING_ERROR_TYPE =
  'PolicyDraftError.ExternalAgentBackdating';

type BackdatingErrorShape = {
  response?: {
    data?: {
      // policy-frontend-api's core router serializes a GenericError directly
      // as the response body (mapErrorResponse: ({ error }) => error), so the
      // discriminating `type` lives at the top level of `data`.
      type?: string;
      // Defensive fallback in case an upstream ever wraps it under `error`.
      error?: {
        type?: string;
      };
    };
  };
};

/**
 * Type guard for the external-agent backdating rejection coming back from the
 * API (HTTP 422). Reads `response.data.type` (the actual serialized
 * GenericError shape) with a defensive `response.data.error.type` fallback,
 * and never throws on a malformed/partial error object.
 */
export function isExternalAgentBackdatingError(err: unknown): boolean {
  const data = (err as BackdatingErrorShape | null | undefined)?.response?.data;
  const type = data?.type ?? data?.error?.type;
  return type === EXTERNAL_AGENT_BACKDATING_ERROR_TYPE;
}
